/* Datum & Ink — the galaxy in the gallery ceiling.

   The room is a plain white gallery with a black hole cut in the roof. Into
   that hole: a nebula wash, a starfield, and a one-point perspective wireframe
   flown slowly away from the viewer and rippled so it reads as space rather
   than as graph paper.

   Colour: everything composites with 'lighter'. On black that is additive, so
   the three RGB passes of the grid sum to white where they overlap and leave
   red / blue fringes where they do not — chromatic aberration, but glowing
   instead of printed. (The previous white-ceiling version used 'multiply' for
   the same reason inverted; neither works on the other background.)

   Geometry comes from hero_layout.json via data attributes, so the sky is
   always locked to the ceiling the renderer actually drew, and the grid runs to
   the same far edge as the room's own architecture. */
(function () {
  var ROWS = 12;
  var COLS = 16;
  var SEGS = 22;          // segments per row, so rows can ripple
  var STARS = 240;
  var FLY = 0.05;         // depth advanced per second
  var FPS = 30;

  function init(root) {
    var canvas = root.querySelector('.di-hero__canvas--sky');
    var art = root.querySelector('.di-hero__art');
    if (!canvas || !art) return;

    var ceiling;
    try {
      ceiling = JSON.parse(root.dataset.ceiling);
    } catch (e) {
      return;
    }
    if (!ceiling || ceiling.length !== 4) return;

    var ctx = canvas.getContext('2d');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var W = 0, H = 0, raf = 0, last = 0, phase = 0, visible = true;
    var nebula = null, stars = [];

    // Ceiling corners, in order: near-left, near-right, far-right, far-left.
    function pt(i) { return { x: ceiling[i][0] * W, y: ceiling[i][1] * H }; }
    function farY() { return pt(3).y || 1; }

    // Depth u >= 0, where 0 is directly overhead and u -> inf recedes to the
    // far edge. The projective spacing is what bunches rows toward the horizon.
    function rowY(u) { return farY() * u / (1 + u); }

    function rowSpan(y) {
      var t = y / farY();
      return {
        x0: pt(0).x + (pt(3).x - pt(0).x) * t,
        x1: pt(1).x + (pt(2).x - pt(1).x) * t
      };
    }

    function buildStars() {
      stars = [];
      var far = farY();
      for (var i = 0; i < STARS; i++) {
        // Same projective bias as the grid, so stars crowd toward the horizon.
        var u = Math.pow(Math.random(), 0.7) * 9;
        var y = far * u / (1 + u);
        var s = rowSpan(y);
        var depth = y / far;                       // 0 overhead, 1 far away
        stars.push({
          x: s.x0 + Math.random() * (s.x1 - s.x0),
          y: y,
          r: (0.4 + Math.random() * 1.5) * (1 - depth * 0.55),
          b: 0.25 + Math.random() * 0.75,
          ph: Math.random() * Math.PI * 2,
          sp: 0.4 + Math.random() * 1.6
        });
      }
    }

    // Pre-rendered once: three big soft clouds. Re-filling radial gradients
    // across the ceiling every frame was the one genuinely expensive thing
    // here, and it does not need to change shape — only drift.
    function buildNebula() {
      var far = Math.max(2, Math.round(farY()));
      var c = document.createElement('canvas');
      c.width = Math.max(2, Math.round(W));
      c.height = far;
      var g = c.getContext('2d');
      var blobs = [
        [0.24, 0.30, 0.52, 'rgba(120,60,255,0.42)'],
        [0.62, 0.58, 0.60, 'rgba(255,60,170,0.32)'],
        [0.86, 0.26, 0.46, 'rgba(60,200,255,0.30)'],
        [0.44, 0.82, 0.44, 'rgba(80,120,255,0.28)']
      ];
      for (var i = 0; i < blobs.length; i++) {
        var bx = blobs[i][0] * c.width;
        var by = blobs[i][1] * c.height;
        var rad = blobs[i][2] * c.width * 0.5;
        var rg = g.createRadialGradient(bx, by, 0, bx, by, rad);
        rg.addColorStop(0, blobs[i][3]);
        rg.addColorStop(1, 'rgba(0,0,0,0)');
        g.fillStyle = rg;
        g.fillRect(0, 0, c.width, c.height);
      }
      nebula = c;
    }

    function resize() {
      var box = art.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = Math.round(box.width * dpr);
      H = Math.round(W * (root.dataset.sceneH / root.dataset.sceneW));
      if (!W || !H) return;
      canvas.width = W;
      canvas.height = H;
      buildStars();
      buildNebula();
      draw(performance.now());
    }

    function clipCeiling() {
      ctx.beginPath();
      ctx.moveTo(pt(0).x, pt(0).y);
      for (var i = 1; i < 4; i++) ctx.lineTo(pt(i).x, pt(i).y);
      ctx.closePath();
      ctx.clip();
    }

    // A slow travelling ripple, strongest overhead and flattening toward the
    // horizon so the far edge still lands on the drawn architecture.
    function warp(x, y, t) {
      var far = farY();
      var k = 1 - y / far;
      return Math.sin(x / W * 5.5 + t * 0.0008 + y / far * 3.0) * far * 0.035 * k * k;
    }

    function strokeGrid(dx, dy, colour, t) {
      ctx.strokeStyle = colour;
      ctx.lineWidth = Math.max(1, W * 0.0008);
      ctx.beginPath();

      var r, c, i, x, y, s, f;
      for (r = 0; r < ROWS; r++) {
        y = rowY(r + phase);
        s = rowSpan(y);
        for (i = 0; i <= SEGS; i++) {
          x = s.x0 + (s.x1 - s.x0) * (i / SEGS);
          var yy = y + warp(x, y, t) + dy;
          if (i === 0) ctx.moveTo(x + dx, yy); else ctx.lineTo(x + dx, yy);
        }
      }

      for (c = 0; c <= COLS; c++) {
        f = c / COLS;
        for (i = 0; i <= SEGS; i++) {
          var g = i / SEGS;
          y = pt(0).y + (pt(3).y - pt(0).y) * g;
          var xa = pt(0).x + (pt(1).x - pt(0).x) * f;
          var xb = pt(3).x + (pt(2).x - pt(3).x) * f;
          x = xa + (xb - xa) * g;
          if (i === 0) ctx.moveTo(x + dx, y + warp(x, y, t) + dy);
          else ctx.lineTo(x + dx, y + warp(x, y, t) + dy);
        }
      }

      ctx.stroke();
    }

    function drawStars(t) {
      ctx.fillStyle = '#ffffff';
      for (var i = 0; i < stars.length; i++) {
        var st = stars[i];
        var tw = 0.55 + 0.45 * Math.sin(t * 0.001 * st.sp + st.ph);
        ctx.globalAlpha = st.b * tw;
        ctx.beginPath();
        ctx.arc(st.x, st.y + warp(st.x, st.y, t), st.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    function draw(t) {
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      clipCeiling();
      ctx.globalCompositeOperation = 'lighter';

      if (nebula) {
        var dx = Math.sin(t * 0.00006) * W * 0.05;
        var dy = Math.cos(t * 0.00009) * farY() * 0.06;
        ctx.globalAlpha = 0.85;
        ctx.drawImage(nebula, dx, dy);
        ctx.globalAlpha = 1;
      }

      drawStars(t);

      // The split breathes so the fringes drift rather than sitting fixed.
      var split = W * 0.0018 * (1 + 0.6 * Math.sin(t * 0.00035));
      var swing = W * 0.0010 * Math.sin(t * 0.00021);
      ctx.globalAlpha = 0.55;
      strokeGrid(-split, swing, '#ff2200', t);
      strokeGrid(0, 0, '#00ff66', t);
      strokeGrid(split, -swing, '#0066ff', t);
      ctx.globalAlpha = 1;

      // Fade the grid and stars into the horizon so the rows do not pile into a
      // bar where the projective spacing collapses.
      var fg = ctx.createLinearGradient(0, farY() * 0.62, 0, farY());
      fg.addColorStop(0, 'rgba(0,0,0,0)');
      fg.addColorStop(1, 'rgba(0,0,0,1)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = fg;
      ctx.fillRect(0, 0, W, farY());

      ctx.restore();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }

    function frame(now) {
      raf = 0;
      if (!visible) return;
      if (now - last >= 1000 / FPS) {
        phase = (phase + FLY * (now - last) / 1000) % 1;
        last = now;
        draw(now);
      }
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (raf || reduced) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }

    function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; }

    resize();
    if (!reduced) start();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) {
        visible = e[0].isIntersecting;
        if (visible) start(); else stop();
      }, { rootMargin: '120px' }).observe(root);
    }

    var timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(resize, 160);
    });
  }

  function boot() {
    var nodes = document.querySelectorAll('.di-hero');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.diSkyReady === '1') continue;
      nodes[i].dataset.diSkyReady = '1';
      init(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
